import { ChallengeState, Mote, MoteUnlockState, User } from '../../models';

/** 
 * Union of a `Mote` (associated with a question in the challenge) and a point amount. 
 * Used in `QuizStat` objects to get extra data for a participant's quiz answers 
 */
interface MotePointUnion {
    key: string;
    mote: Mote;
    points: number;
}

/** 
 * Aggregated statistics for a `Challenge`
 */
export interface QuizOverview {
    participants: number;
    averagePoints: number;
    averageDuration: number;
}

/** 
 * The amount of right and wrong answers for a particular question in the `Challenge`. Used in the quiz overview
 */
export class AnswerDistribution {
    key: string;
    mote: Mote;
    right: number = 0;
    wrong: number = 0;
    percentageTrue?: number;

    constructor(key: string, mote: Mote) {
        this.key = key;
        this.mote = mote;
    }
}

/** 
 * Quiz statistics for a single challenge participant
 */
export class QuizStat {
    // User information
    user: User;
    userName: string;
    email: string;
    // Points, answers and unlocks
    challengeState: ChallengeState;
    points: number;
    answers: MotePointUnion[] = [];
    level: number;
    unlocks: MoteUnlockState[];
    // Start time, end time and duration of the challenge
    start: number;
    end: number;
    duration: number;
    // The rank property is updated externally when this QuizStat is compared to the others in the challenge
    rank?: number;

    /**
     * Create a `QuizStat` object
     * @param user The `User`
     * @param challengeKey Key for the `Challenge` from which data should be extracted
     * @param projectMotes Motes from the current project
     * @returns A `QuizStat` object with select user and challenge data
     */
    constructor(user: User, challengeKey: string, projectMotes: Mote[]) {
        // Extract user information
        this.userName = user.userName;
        this.email = user.email;

        // Get the challenge state for the challenge specified by challengeKey
        this.challengeState = user.challengeStateMap.getChallengeState(challengeKey);
        // Get the total point amount for the challenge
        this.points = this.challengeState.points;
        // Get the user's achieved level
        this.level = this.challengeState.level;

        // Sort unlocked motes based on time
        this.unlocks = user?.unlockedMotes.sort((a, b) => a.createdOn - b.createdOn);
        // Set start time from first mote in array
        this.start = this.unlocks.length > 0 ? this.unlocks[0].createdOn : 0;
        // Set end time from last mote in array
        this.end = this.unlocks.length > 0 ? this.unlocks[this.unlocks.length - 1].createdOn : 0;
        // Convert to seconds
        this.duration = (this.end - this.start) / 1000;

        // Iterate over the mapped pointMap type and push values to answers array
        this.answers = Object.keys(this.challengeState.pointMap).map((key) => {
            return {
                key,
                mote: projectMotes.find((mote) => mote.key == key),
                points: this.challengeState.pointMap[key],

            }
        });
        // Sort the motes based on specified order
        this.answers.sort((a, b) => a.mote.order - b.mote.order);
    }
}
